/* Kandidaat-kaart (grid-view) — dual-action: body opent profiel, checkbox
 * selecteert voor shortlist. Incl. pool-badge, bronvermelding en on-demand
 * LinkedIn-verrijking (Apify). EmptyPool = lege-staat van de lijst. */
import React, { useState } from "react";
import i18n from "i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search as SearchIcon, Mail, Wand2, Edit3, Trash2, Radar, Loader2 } from "lucide-react";
import CandidateAvatar from "@/components/CandidateAvatar";
import { LinkedInButton } from "@/components/common/LinkedInButton";
import { PoolBadge } from "@/components/recruit/PoolBadge";
import { SOURCED_BY_LABEL } from "@/components/recruit/SourceSelector";

export default function CandidateCard({ c, isPicked, isFresh, poolName, onToggle, onOpen, onEdit, onDelete, onEnriched }) {
    const [enriching, setEnriching] = useState(false);
    const doEnrich = async (e) => {
        e.stopPropagation();
        setEnriching(true);
        try {
            await api.post(`/profile-resolver/deep-enrich/${c.id}`);
            toast.success(i18n.t("search.enriched_ok", { name: c.name }));
            onEnriched?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || i18n.t("search.enrich_failed"));
        } finally { setEnriching(false); }
    };
    return (
        <div data-testid={`candidate-${c.id}`}
            className={`relative rounded-xl border p-4 transition-all hover:-translate-y-[1px] ${isPicked
                ? "border-[var(--jr-amber)] bg-[var(--jr-amber)]/10"
                : "border-white/8 bg-[var(--jr-surface)] hover:border-white/14"}`}>
            {/* CRUD row (top-right, above the pick checkbox) */}
            {(onEdit || onDelete) && (
                <div className="absolute top-3 right-10 flex items-center gap-0.5 z-10">
                    {onEdit && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(); }}
                            data-testid={`candidate-edit-${c.id}`} title={i18n.t("common.edit")}
                            className="p-1 rounded text-[var(--jr-text-3)] hover:text-white hover:bg-white/8">
                            <Edit3 size={12}/>
                        </button>
                    )}
                    {onDelete && (
                        <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(); }}
                            data-testid={`candidate-delete-${c.id}`} title={i18n.t("common.delete")}
                            className="p-1 rounded text-[var(--jr-text-3)] hover:text-red-400 hover:bg-red-500/10">
                            <Trash2 size={12}/>
                        </button>
                    )}
                </div>
            )}
            {/* Multi-select checkbox top-right (does NOT trigger drawer) */}
            <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }}
                data-testid={`candidate-pick-${c.id}`}
                title={isPicked ? i18n.t("search.deselect") : i18n.t("search.select_for_shortlist")}
                className={`absolute top-3 right-3 w-5 h-5 rounded border-2 grid place-items-center transition-colors z-10 ${isPicked
                    ? "bg-[var(--jr-amber)] border-[var(--jr-amber)]"
                    : "border-white/20 hover:border-white/40 bg-[var(--jr-surface-2)]"}`}>
                {isPicked && <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
            </button>

            {/* Body — clickable → opens drawer */}
            <button type="button" onClick={onOpen} data-testid={`candidate-open-${c.id}`} className="w-full text-left">
                <div className="flex items-start gap-3 pr-8">
                    <CandidateAvatar name={c.name} url={c.photo_url} size={48} testid={`candidate-avatar-${c.id}`} />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-display text-base text-white truncate">{c.name}</span>
                            <PoolBadge poolKey={c.talent_pool} poolName={poolName} testid={`candidate-pool-${c.id}`} />
                            {isFresh && (
                                <span className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded bg-[var(--jr-success)]/15 text-[var(--jr-success)]"
                                    title={i18n.t("search.fresh_tip")}>{i18n.t("common.new")}</span>
                            )}
                        </div>
                        <p className="text-xs text-[var(--jr-text-2)] truncate">{c.title}</p>
                        <p className="text-[11px] text-[var(--jr-text-3)] mt-0.5 truncate">{c.current_company || i18n.t("search.free_agent")} · {c.location}</p>
                    </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                    {(c.skills || []).slice(0, 4).map((s, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-[var(--jr-text-2)]">{s}</span>
                    ))}
                </div>
                <div className="mt-2 flex items-center gap-2 text-[10px] text-[var(--jr-text-3)] font-mono">
                    <span className="uppercase tracking-[0.14em]">via {SOURCED_BY_LABEL[c.sourced_by] || c.sourced_by || "manual"}</span>
                    {(c.saved_search_ids || []).length > 1 && (
                        <span className="text-[var(--jr-amber)] uppercase tracking-[0.14em]" title={i18n.t("search.multi_search_tip")}>
                            in {c.saved_search_ids.length} searches
                        </span>
                    )}
                </div>
            </button>

            {/* Contact strip — LinkedIn-knop ALTIJD zichtbaar (gevonden of niet) */}
            <div className="mt-3 pt-3 border-t border-white/8 flex items-center gap-1.5">
                <LinkedInButton url={c.linkedin_url} name={c.name} variant="icon" testid={`candidate-linkedin-${c.id}`} resolve={{ type: "candidate", id: c.id }} />
                {c.email && (
                    <a href={`mailto:${c.email}`}
                        onClick={(e) => e.stopPropagation()}
                        title={c.email}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[var(--jr-info)]/10 border border-[var(--jr-info)]/30 text-[var(--jr-info)] hover:bg-[var(--jr-info)]/20 transition-colors">
                        <Mail size={12}/>
                    </a>
                )}
                {c.linkedin_url && (
                    <button type="button" onClick={doEnrich} disabled={enriching}
                        data-testid={`candidate-enrich-${c.id}`}
                        title={i18n.t("search.enrich_tip")}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#a855f7]/10 border border-[#a855f7]/30 text-[#a855f7] hover:bg-[#a855f7]/20 transition-colors disabled:opacity-50">
                        {enriching ? <Loader2 size={12} className="animate-spin"/> : <Radar size={12}/>}
                    </button>
                )}
                <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(); }}
                    className="ml-auto text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hover:text-white">
                    Open profiel →
                </button>
            </div>
        </div>
    );
}

export function EmptyPool({ onRun, disabled }) {
    return (
        <div className="rounded-xl border border-dashed border-white/12 p-10 text-center" data-testid="talent-empty">
            <SearchIcon size={32} className="mx-auto text-[var(--jr-text-3)] mb-3" />
            <h3 className="text-lg font-display text-white mb-1">{i18n.t("search.empty_title")}</h3>
            <p className="text-sm text-[var(--jr-text-2)] max-w-md mx-auto mb-4">
                {i18n.t("search.empty_body_pre")} <span className="text-[var(--jr-amber)] font-medium">{i18n.t("search.search_btn")}</span>{i18n.t("search.empty_body_post")}
            </p>
            <button onClick={onRun} disabled={disabled} data-testid="talent-empty-run"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-amber)] text-black text-sm font-medium hover:bg-[var(--jr-amber)]/85 disabled:opacity-50">
                <Wand2 size={14} /> {i18n.t("search.search_btn")}
            </button>
        </div>
    );
}
